/**
 * @file: WiredMessageList.tsx
 *
 * State wrapper for the pure {@link MessageList}: subscribes the conversation's
 * `messages$` / `loading$` (domain) and the coordinator's
 * `scroll.highlightedMessageId$`, binds the list to the coordinator's scroll
 * controller, and builds the row renderer from the coordinator's bubble
 * interaction handlers. Renders the initial spinner until the first page
 * lands.
 */

import { useChatConversation } from '@euler/app/flows/chat/lib/conversation/context';
import { isGroupRoom } from '@euler/app/flows/chat/lib/conversation/handle';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMsg } from '@euler/model/chat';
import { idEquals } from '@euler/utils';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  LayoutChangeEvent,
  ListRenderItem,
  View,
} from 'react-native';
import {
  BUBBLE_COLUMN_FRACTION,
  MessageBubble,
} from '../message/MessageBubble';
import { MESSAGE_LIST_TIME_MARKER_FIRST, MessageList } from './MessageList';

export const WiredMessageList: FC<{ interactive?: boolean }> = memo(
  ({ interactive = true }) => {
    const { rpx } = useResponsiveContext();
    const conversation = useChatConversation();
    const { scroll } = conversation.coordinator;
    const [messages] = useBehaviorSubject(conversation.messages$);
    const [highlightedMessageId] = useBehaviorSubject(
      scroll.highlightedMessageId$,
    );
    const [loading] = useBehaviorSubject(conversation.loading$);
    const { me } = useChatSession();
    const myPlatformUserId = me.platformUserId;
    const groupRoom = isGroupRoom(conversation.conversationHandle);

    // Measure the list width ONCE — the row width is identical for every
    // bubble, so the bubble's px max-width is computed here, not per-cell. (A
    // `%` cap can't be used: FlashList's non-deterministic cell sizing drops
    // it.) `rpx(28)` = the list's + row's horizontal padding (2·12 + 2·2).
    const [listWidth, setListWidth] = useState(0);
    const onListLayout = useCallback((e: LayoutChangeEvent) => {
      const w = e.nativeEvent.layout.width;
      setListWidth(prev => (prev === w ? prev : w));
    }, []);
    const maxBubbleWidth = useMemo(
      () =>
        listWidth > 0
          ? Math.round((listWidth - rpx(28)) * BUBBLE_COLUMN_FRACTION)
          : undefined,
      [listWidth, rpx],
    );
    const {
      bubbleMenu,
      onReact,
      onPreviewImage,
      onPreviewVideo,
      onOpenUrl,
      onOpenCard,
      onQuotePress,
      onRetry,
      onShowReaders,
      onAudioPlay,
      onSuggestionPress,
    } = conversation.coordinator;

    const renderItem: ListRenderItem<ChatMsg> = useCallback(
      ({ item, index }) => {
        // Inverted list is newest-first, so the chronologically-previous
        // (older) message is the NEXT element. The oldest message has no older
        // neighbour → time marker shows.
        const prev = index < messages.length - 1 ? messages[index + 1] : null;
        return (
          <MessageBubble
            message={item}
            isMine={idEquals(item.authorId, myPlatformUserId)}
            myPlatformUserId={myPlatformUserId}
            roomType={groupRoom ? 'group' : 'private'}
            prevMessageCreatedAt={prev?.createdAt ?? null}
            highlighted={
              item.kind === 'remote' && highlightedMessageId === item.messageId
            }
            maxBubbleWidth={maxBubbleWidth}
            timeMarkerFirst={MESSAGE_LIST_TIME_MARKER_FIRST}
            style={{ marginBottom: rpx(8) }}
            // Dismissed room → read-only: no long-press menu, no reactions.
            onPresentMenu={interactive ? bubbleMenu.present : undefined}
            onReact={interactive ? onReact : undefined}
            onPreviewImage={onPreviewImage}
            onPreviewVideo={onPreviewVideo}
            onOpenUrl={onOpenUrl}
            onOpenCard={onOpenCard}
            onQuotePress={onQuotePress}
            onRetry={onRetry}
            onShowReaders={onShowReaders}
            onAudioPlay={onAudioPlay}
            onSuggestionPress={onSuggestionPress}
          />
        );
      },
      [
        interactive,
        messages,
        myPlatformUserId,
        groupRoom,
        highlightedMessageId,
        maxBubbleWidth,
        rpx,
        bubbleMenu,
        onReact,
        onPreviewImage,
        onPreviewVideo,
        onOpenUrl,
        onOpenCard,
        onQuotePress,
        onRetry,
        onShowReaders,
        onAudioPlay,
        onSuggestionPress,
      ],
    );

    // The onLayout wrapper is ALWAYS mounted so the list width is measured up
    // front (even during the initial load). Messages render only once that
    // width is known — otherwise bubbles would paint at the `80%` fallback for
    // a frame and snap to the measured px cap (a visible flash). The spinner
    // covers both the initial fetch and the pre-measure gap.
    const ready = listWidth > 0 && !(loading && messages.length === 0);
    return (
      <View style={{ flex: 1 }} onLayout={onListLayout}>
        {ready ? (
          <MessageList
            style={{ flex: 1 }}
            messages={messages}
            renderItem={renderItem}
            flatListRef={scroll.listRef}
            onScroll={scroll.onScroll}
            onScrollToIndexFailed={scroll.onScrollToIndexFailed}
            onEndReached={conversation.loadOlder}
            loadingOlder$={conversation.loadingOlder$}
          />
        ) : (
          <View
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
          >
            <ActivityIndicator />
          </View>
        )}
      </View>
    );
  },
);
